/**
 * resolveThemeTokens.js — o ÚNICO lugar que decide o que o tema do nicho pinta no painel.
 *
 * Regra (docs/ANALISE-TEMAS-MARCA-VS-OPERACAO.md, D14): o painel é ESCALA OPERACIONAL (global, por
 * modo) + ACENTO do nicho (por modo). Do tema, esta função lê SÓ `accent[modo]` e `fontHeading`.
 * Qualquer outra chave que um JSON de tema traga (fundo, texto, estados…) é IGNORADA — é isso que
 * impede a marca de fachada (escura, contrastada) de vazar para a área de dados.
 *
 * Pura: sem React, sem DOM. Testada em __tests__/resolveThemeTokens.test.js.
 */
import { operationalModes } from './operationalModes.js';
import { ACCENT_KEYS, ACCENT_TO_VAR, accentDefaults, fontHeading as defaultFontHeading } from './designTokens.js';

/** 'dark' | 'light' — qualquer outro valor vira 'light'. */
export const normalizeColorMode = (mode) => (mode === 'dark' ? 'dark' : 'light');

const isHex6 = (v) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v);

/**
 * Acento do modo, chave a chave. Tema incompleto NÃO herda do Padrão as chaves que dependem da
 * matiz do próprio tema (fundo suave, texto sobre a primária, tom de marca no drawer) — senão o
 * índigo do Padrão vazaria num tema vermelho. Essas são derivadas da primária do tema.
 */
function resolveAccent(themeAccent, mode) {
  const src = themeAccent || {};
  const fallback = accentDefaults[mode];
  const primary = src.primary ?? fallback.primary;
  const derived = {
    primarySoft: isHex6(primary) ? `${primary}${mode === 'dark' ? '2E' : '1F'}` : fallback.primarySoft,
    onPrimary: '#FFFFFF',
    drawerAccent: src.primaryDark ?? primary,
  };
  return Object.fromEntries(ACCENT_KEYS.map((key) => {
    if (src[key] != null) return [key, src[key]];
    if (src.primary != null && key in derived) return [key, derived[key]];
    return [key, fallback[key]];
  }));
}

/**
 * @param {Object|null|undefined} adminSection seção `admin` do {id}.json (já mesclada com o default)
 * @param {'light'|'dark'} requestedMode modo efetivo (preferência do usuário ou `defaultMode` do tema)
 * @returns {{
 *   mode: 'light'|'dark',
 *   accent: Object,
 *   scale: Object,
 *   fontHeading: string,
 *   cssVars: Object,
 *   colors: Object,
 * }}
 *   - `cssVars`: o que o ThemeApplier emite inline no :root (acento + --font-heading). A escala
 *     NÃO entra: ela vem do tokens.css pelo atributo data-color-mode.
 *   - `colors`: mapa plano no vocabulário que `getColor()` sempre serviu (primary, background,
 *     backgroundPaper, text, textSecondary, divider, success…), para o legado continuar lendo.
 */
export function resolveThemeTokens(adminSection, requestedMode) {
  const mode = normalizeColorMode(requestedMode);
  const scale = operationalModes[mode];
  const accent = resolveAccent(adminSection?.accent?.[mode], mode);
  const fontHeading = adminSection?.fontHeading || defaultFontHeading;

  const cssVars = { '--font-heading': fontHeading };
  for (const key of ACCENT_KEYS) cssVars[ACCENT_TO_VAR[key]] = accent[key];

  const colors = {
    ...accent,
    backgroundDrawer: accent.drawerBackground,
    textDrawer: accent.drawerText,
    background: scale['--bg-page'],
    backgroundPaper: scale['--surface'],
    text: scale['--text-primary'],
    textSecondary: scale['--text-secondary'],
    textDisabled: scale['--text-disabled'],
    divider: scale['--divider'],
    success: scale['--color-success'],
    warning: scale['--color-warning'],
    error: scale['--color-error'],
    info: scale['--color-info'],
  };

  return { mode, accent, scale, fontHeading, cssVars, colors };
}

/** Escreve no :root as CSS vars de acento + título resolvidas (`resolveThemeTokens(...).cssVars`). */
export function applyThemeCssVars(cssVars) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.entries(cssVars).forEach(([name, value]) => {
    if (value != null) root.style.setProperty(name, value);
  });
}

/** Marca o modo no <html>: o tokens.css troca a escala por `:root[data-color-mode="dark"]`. */
export function applyColorModeAttribute(mode) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.colorMode = normalizeColorMode(mode);
}
